import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';
import SearchIcon from '@material-ui/icons/Search';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import TuneIcon from '@material-ui/icons/Tune';
import { ReactComponent as Logo } from '../../../assets/images/kealogo-text-bg.svg';
import FacetEditorPrototype from './fixtureFacetEditorPrototype';
import FacetOrderBoard from './fixtureFacetOrderBoard';
import { FACETS, FACET_ZONES } from './fixtureFacetPrototypeData';
import './FacetsPrototype.scss';

const BASE = '/prototype/facets';

const cloneZones = zones => Object.keys(zones).reduce((copy, key) => ({
  ...copy,
  [key]: [...zones[key]],
}), {});

const FacetsPrototype = () => {
  const history = useHistory();
  const location = useLocation();
  const [zones, setZones] = useState(() => cloneZones(FACET_ZONES));
  const [savedZones, setSavedZones] = useState(() => cloneZones(FACET_ZONES));
  const [facetConfigs, setFacetConfigs] = useState({});
  const pathParts = location.pathname.split('/').filter(Boolean);
  const facetId = pathParts.length > 2 ? pathParts[2] : null;
  const facets = useMemo(() => Object.keys(FACETS).reduce((result, id) => ({
    ...result,
    [id]: facetConfigs[id] ? { ...FACETS[id], ...facetConfigs[id].draft } : FACETS[id],
  }), {}), [facetConfigs]);
  const facet = facetId ? facets[facetId] : null;
  const dirty = useMemo(() => JSON.stringify(zones) !== JSON.stringify(savedZones), [savedZones, zones]);

  useEffect(() => {
    const previousLanguage = document.documentElement.lang;
    document.documentElement.lang = 'en';
    return () => { document.documentElement.lang = previousLanguage; };
  }, []);

  return (
    <div className="facets-prototype">
      <header className="fp-topbar" aria-hidden={Boolean(facet)}>
        <Link to={BASE} className="fp-brand" aria-label="Kea Labs"><Logo /></Link>
        <div className="fp-prototype-label"><strong>Facets UX prototype</strong><span>No store data is changed</span></div>
        <div className="fp-store"><span>D</span><div><strong>Demo Store</strong><small>Search administration</small></div></div>
      </header>
      <div className="fp-shell" aria-hidden={Boolean(facet)}>
        <aside className="fp-sidebar">
          <nav aria-label="Search administration">
            <span className="fp-nav-item"><HomeOutlinedIcon /><span>Search</span></span>
            <span className="fp-nav-item"><AssessmentOutlinedIcon /><span>Analytics</span></span>
            <span className="fp-nav-item"><SettingsOutlinedIcon /><span>Settings</span></span>
            <div className="fp-nav-divider" />
            <span className="fp-nav-heading"><TuneIcon /><span>Customization</span></span>
            <Link className="fp-nav-item is-active" to={BASE}><SearchIcon /><span>Filters</span></Link>
            <span className="fp-nav-item is-muted"><span className="fp-nav-dot" /><span>Empty query</span></span>
            <span className="fp-nav-item is-muted"><span className="fp-nav-dot" /><span>Merchandising</span></span>
          </nav>
          <div className="fp-prototype-note"><strong>Prototype</strong><span>Explore order, mapping and value editors with fixture data.</span></div>
        </aside>
        <main className="fp-main">
          <FacetOrderBoard
            facets={facets}
            zones={zones}
            onZonesChange={setZones}
            onOpen={id => history.push(`${BASE}/${id}`)}
            onReset={() => setZones(cloneZones(savedZones))}
            onSave={() => setSavedZones(cloneZones(zones))}
            dirty={dirty}
          />
        </main>
      </div>
      {facet && (
        <FacetEditorPrototype
          facet={facet}
          savedConfig={facetConfigs[facet.id]}
          onSave={config => setFacetConfigs(current => ({ ...current, [facet.id]: config }))}
          onClose={() => history.push(BASE)}
        />
      )}
    </div>
  );
};

export default FacetsPrototype;
